import {
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import SystemAdminApi from "../../../infrastructure/api/systemAdmin";

const REFRESH_MS = 30000;

const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "healthy") return "success";
  if (s === "degraded") return "warning";
  return "error";
};

const StatusIcon = ({ status }) => {
  const s = (status || "").toLowerCase();
  if (s === "healthy") return <HealthyIcon color="success" />;
  if (s === "degraded") return <WarningIcon color="warning" />;
  return <ErrorIcon color="error" />;
};

const SystemHealthPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, metricsRes] = await Promise.all([
        SystemAdminApi.getHealth(),
        SystemAdminApi.getMetrics(),
      ]);
      const healthNorm = SystemAdminApi.normalize(
        healthRes,
        "بارگذاری وضعیت سلامت ناموفق بود."
      );
      const metricsNorm = SystemAdminApi.normalize(
        metricsRes,
        "بارگذاری متریک‌ها ناموفق بود."
      );
      if (healthNorm.error) {
        setError(healthNorm.error);
        setHealth(null);
      } else {
        setHealth(healthNorm.data);
      }
      if (!metricsNorm.error) {
        setMetrics(metricsNorm.data);
      }
      setLastCheck(new Date());
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری وضعیت سیستم");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const overall = health?.status ?? health?.Status ?? "Unknown";
  const checks = health?.checks ?? health?.Checks ?? {};
  const checkEntries = Object.entries(checks);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            سلامت سیستم
          </Typography>
          <Typography variant="body2" color="text.secondary">
            مانیتورینگ عملیاتی — بروزرسانی خودکار هر ۳۰ ثانیه
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={load}
          disabled={loading}
        >
          بروزرسانی
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <StatusIcon status={overall} />
                <Typography variant="h6">وضعیت کلی</Typography>
              </Box>
              <Chip label={overall} color={statusColor(overall)} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                آخرین بررسی:{" "}
                {lastCheck ? lastCheck.toLocaleString("fa-IR") : "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {metrics && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={1}>
                  متریک‌های عملیاتی
                </Typography>
                <Grid container spacing={1}>
                  {[
                    ["درخواست / دقیقه", metrics.requestsPerMinute ?? metrics.RequestsPerMinute],
                    ["خطاها", metrics.errorCount ?? metrics.ErrorCount],
                    ["OTP ارسال", metrics.otpSent ?? metrics.OtpSent],
                    ["OTP ناموفق", metrics.otpFailed ?? metrics.OtpFailed],
                    ["OTP مسدود", metrics.otpBlocked ?? metrics.OtpBlocked],
                    ["OTP محدود", metrics.otpRateLimited ?? metrics.OtpRateLimited],
                    ["خطای ورود", metrics.loginFailures ?? metrics.LoginFailures],
                    ["کاربر فعال (تقریبی)", metrics.activeUsersApprox ?? metrics.ActiveUsersApprox],
                    [
                      "دامنه متریک",
                      (metrics.isClusterWide ?? metrics.IsClusterWide) ? "کل استقرار (Redis)" : "فقط این نمونه",
                    ],
                  ].map(([label, value]) => (
                    <Grid item xs={6} sm={4} md={3} key={label}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="h6">{value ?? 0}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            جزئیات بررسی‌ها
          </Typography>
          {loading && !health ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>مؤلفه</TableCell>
                  <TableCell>وضعیت</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checkEntries.map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ textTransform: "uppercase" }}>{key}</TableCell>
                    <TableCell>{value}</TableCell>
                  </TableRow>
                ))}
                {checkEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      داده‌ای موجود نیست
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemHealthPage;
